import React, { useState, useEffect, useRef } from 'react';
import { questionAPI } from '../services/api';
import AdminSidebar from "../components/AdminSidebar";
import QuestionModal from './QuestionModal';
import AdminNavbar from '../components/AdminNavbar'; 
import { useSidebar } from '../components/SidebarContext';
import Filter from './Filter';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

const hasLatex = (text) => {
  if (!text || typeof text !== 'string') {
    console.log(`No LaTeX: Text is ${text}`);
    return false;
  }
  // More specific LaTeX patterns to avoid false positives with standalone underscores
  const latexPatterns = [
    /\\([a-zA-Z]+)(?:\s|{)/, // Matches LaTeX commands like \frac, \sqrt, etc.
    /\^{[^}]+}/, // Matches superscripts like ^{2}
    /_{[^}]+}/, // Matches subscripts like _{n}
    /\\\[.*?\\\]/, // Matches display math like \[...\]
    /\\\(.*?\\\)/, // Matches inline math like \(...\)
    /\$.*?\$/, // Matches math enclosed in single dollar signs
  ];
  const result = latexPatterns.some(pattern => pattern.test(text));
  console.log(`Checking LaTeX in: "${text}" -> ${result}`);
  return result;
};

const renderTextWithLatex = (text, isBlock = false) => {
  if (!text || typeof text !== 'string') return text || 'Not available';

  if (hasLatex(text)) {
    try {
      // Remove dollar signs for KaTeX rendering, but do not escape underscores
      let cleanedText = text.replace(/(\$\$|\$)/g, '');
      return isBlock ? (
        <BlockMath math={cleanedText} />
      ) : (
        <InlineMath math={cleanedText} />
      );
    } catch (error) {
      console.error(`Error rendering LaTeX for text "${text}":`, error);
      return text; // Fallback to plain text if LaTeX rendering fails
    }
  }
  // Return plain text without modification for underscores
  return text;
};

function QuestionManager() {
  const { isSidebarOpen } = useSidebar();
  const sidebarWidth = isSidebarOpen ? '15.6rem' : '4.4rem';

  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({ section: null, difficulty: null });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [tempFilters, setTempFilters] = useState({ section: null, difficulty: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const questionsPerPage = 100;
  const [notifications, setNotifications] = useState([]);

  const tableContainerRef = useRef(null);

  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTo(0, 0);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchQuestions();
  }, []);

  useEffect(() => {
    if (notifications.length > 0) {
      const timers = notifications.map((notification, index) =>
        setTimeout(() => {
          setNotifications((prev) => prev.filter((_, i) => i !== index));
        }, 5000)
      );
      return () => timers.forEach((timer) => clearTimeout(timer));
    }
  }, [notifications]);

  const showNotification = (message, type = 'success') => {
    const newNotification = { message, type, id: Date.now() };
    setNotifications((prev) => [...prev, newNotification]);
  };

  const handleCloseNotification = (id) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== id));
  };

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { t: Date.now() };
      console.log('Fetching questions: params=', JSON.stringify(params));
      const res = await questionAPI.getAll({ params });
      console.log('API response:', JSON.stringify({
        dataLength: res.data.data?.length,
        total: res.data.total,
        firstQuestion: res.data.data?.[0]?.questionText || 'No questions',
        firstQuestionId: res.data.data?.[0]?._id || 'No ID',
        response: res.data
      }, null, 2));
      if (!res.data.data?.length && res.data.total > 0) {
        console.warn('No questions returned.', 'Total:', res.data.total);
      }
      const fetchedQuestions = res.data.data || [];
      setQuestions(fetchedQuestions);
      applyFilters(fetchedQuestions, selectedFilters, searchQuery, false);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching questions:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setError('Failed to load questions. Please try again later.');
      setLoading(false);
    }
  };

  const applyFilters = (questionsToFilter, filters, query = '', preservePage = false) => {
    let filtered = [...questionsToFilter];
    if (filters.section) {
      filtered = filtered.filter(q => (q.section || q.part) === filters.section);
    }
    if (filters.difficulty) {
      filtered = filtered.filter(q => (q.difficulty || 'easy').toLowerCase() === filters.difficulty.toLowerCase());
    }
    if (query) {
      filtered = filtered.filter(q =>
        (q.questionText || q.text || '').toLowerCase().includes(query.toLowerCase())
      );
    }
    setFilteredQuestions(filtered);
    if (!preservePage) {
      setCurrentPage(1);
    } else {
      const totalPages = Math.ceil(filtered.length / questionsPerPage);
      if (currentPage > totalPages) {
        setCurrentPage(Math.max(1, totalPages));
      }
    }
  };

  const indexOfLastQuestion = currentPage * questionsPerPage;
  const indexOfFirstQuestion = indexOfLastQuestion - questionsPerPage;
  const currentQuestions = filteredQuestions.slice(indexOfFirstQuestion, indexOfLastQuestion);
  const totalPages = Math.ceil(filteredQuestions.length / questionsPerPage);

  const paginate = (pageNumber) => {
    const maxPage = Math.min(pageNumber, totalPages);
    setCurrentPage(maxPage > 0 ? maxPage : 1);
    setIsDropdownOpen(false);
  };

  const getVisiblePages = () => {
    const visibleCount = 10;
    const start = 1;
    const end = Math.min(start + visibleCount - 1, totalPages);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const handleFilterChange = (newFilters) => {
    setTempFilters(newFilters);
  };

  const applyFilterModal = () => {
    setSelectedFilters(tempFilters);
    applyFilters(questions, tempFilters, searchQuery);
    setShowFilterModal(false);
    setNotifications([]);
  };

  const handleOpenFilterModal = () => {
    setTempFilters(selectedFilters);
    setShowFilterModal(true);
  };

  const handleCloseFilterModal = () => {
    setShowFilterModal(false);
    setTempFilters(selectedFilters);
    setNotifications([]);
  };

  const removeFilter = (type) => {
    const newFilters = { ...selectedFilters, [type]: null };
    setSelectedFilters(newFilters);
    applyFilters(questions, newFilters, searchQuery);
  };

  const clearAllFilters = () => {
    const newFilters = { section: null, difficulty: null };
    setSelectedFilters(newFilters);
    applyFilters(questions, newFilters, searchQuery);
  };

  const handleAddQuestion = () => {
    setSelectedQuestion(null);
    setShowModal(true);
    console.log('Opening modal to add a new question');
  };

  const handleEdit = (question) => {
    const questionToEdit = {
      ...question,
      _id: question._id,
      questionText: question.questionText || question.text || '',
      options: question.options || [],
      correctAnswer: question.correctAnswer || '',
      section: question.section || question.part || '',
      difficulty: question.difficulty || 'easy'
    };
    console.log('Editing question:', questionToEdit);
    setSelectedQuestion(questionToEdit);
    setShowModal(true);
  };

  const handleView = (question) => {
    setSelectedQuestion(question);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedQuestion(null);
    setNotifications([]);
  };

  const handleModalCloseRequest = () => {
    setSelectedQuestion(null);
    setShowModal(false);
    setNotifications([]);
  };

  const handleSaveQuestion = async (data) => {
    try {
      console.log('Saving question data:', data);
      const questionData = {
        questionText: data.questionText,
        options: data.options,
        correctAnswer: data.correctAnswer,
        section: data.section,
        difficulty: data.difficulty
      };

      const currentPageBeforeSave = currentPage;
      if (selectedQuestion) {
        await questionAPI.update(selectedQuestion._id, questionData);
        console.log('Question updated successfully');
        showNotification('Question updated successfully', 'success');
      } else {
        await questionAPI.create(questionData);
        console.log('Question created successfully');
        showNotification('Question added successfully', 'success');
      }
      setShowModal(false);

      const res = await questionAPI.getAll({ params: { t: Date.now() } });
      const fetchedQuestions = res.data.data || [];
      setQuestions(fetchedQuestions);

      applyFilters(fetchedQuestions, selectedFilters, searchQuery, true);

      const editedQuestionId = selectedQuestion?._id;
      if (editedQuestionId) {
        const filtered = fetchedQuestions.filter(q => {
          let matches = true;
          if (selectedFilters.section) {
            matches = matches && (q.section || q.part) === selectedFilters.section;
          }
          if (selectedFilters.difficulty) {
            matches = matches && (q.difficulty || 'easy').toLowerCase() === selectedFilters.difficulty.toLowerCase();
          }
          if (searchQuery) {
            matches = matches && (q.questionText || q.text || '').toLowerCase().includes(searchQuery.toLowerCase());
          }
          return matches;
        });
        const questionIndex = filtered.findIndex(q => q._id === editedQuestionId);
        if (questionIndex !== -1) {
          const targetPage = Math.floor(questionIndex / questionsPerPage) + 1;
          if (targetPage !== currentPageBeforeSave) {
            setCurrentPage(targetPage);
          }
        } else {
          const totalPages = Math.ceil(filtered.length / questionsPerPage);
          if (currentPageBeforeSave > totalPages) {
            setCurrentPage(Math.max(1, totalPages));
          } else {
            setCurrentPage(currentPageBeforeSave);
          }
        }
      }
    } catch (error) {
      console.error('Error saving question:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      showNotification(`Failed to save question: ${error.response?.data?.error || error.message}`, 'error');
      throw error;
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      try {
        await questionAPI.delete(id);
        console.log('Question deleted successfully');
        showNotification('Question deleted successfully', 'success');
        const currentPageBeforeDelete = currentPage;
        await fetchQuestions();
        const totalPages = Math.ceil(filteredQuestions.length / questionsPerPage);
        if (currentPageBeforeDelete > totalPages) {
          setCurrentPage(Math.max(1, totalPages));
        } else {
          setCurrentPage(currentPageBeforeDelete);
        }
      } catch (error) {
        console.error('Error deleting question:', error);
        showNotification('Failed to delete the question. Please try again.', 'error');
      }
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    applyFilters(questions, selectedFilters, query);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const sections = [...new Set(questions.map(q => q.section || q.part).filter(Boolean))];
  const difficulties = [...new Set(questions.map(q => (q.difficulty || 'easy').toLowerCase()))];

  const styles = {
    container: {
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      minHeight: '100vh',
      background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)',
      fontFamily: "'Roboto', sans-serif",
    },
    sidebar: {
      width: sidebarWidth,
      borderRadius: '30px',
      height: '100vh',
      flexShrink: 0,
      fontFamily: "'Roboto', sans-serif",
    },
    content: {
      flex: 1,
      padding: '15px',
      margin: '6rem 2rem 2rem 2rem',
      backgroundColor: 'white',
      overflowY: 'auto',
      transition: 'margin-left 0.3s ease',
      minHeight: 'calc(100vh - 8rem)',
      borderTopLeftRadius: '1rem',
      borderTopRightRadius: '1rem',
      borderBottomLeftRadius: '1rem',
      borderBottomRightRadius: '1rem',
      maxWidth: `calc(100vw - ${sidebarWidth} - 4rem)`,
      width: '100%',
      position: 'relative',
      fontFamily: "'Roboto', sans-serif",
    },
    notificationContainer: {
      position: 'absolute',
      top: '15px',
      right: '30px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      zIndex: 100,
      fontFamily: "'Roboto', sans-serif",
    },
    notification: {
      fontSize: '0.85rem',
      color: '#6B21A8',
      fontWeight: '400',
      backgroundColor: '#F3E8FF',
      padding: '5px 10px',
      borderRadius: '4px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      maxWidth: '300px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      animation: 'slideInFromRight 0.5s ease-out',
      fontFamily: "'Roboto', sans-serif",
    },
    notificationSuccess: {
      backgroundColor: '#F3E8FF',
      color: '#6B21A8',
      fontFamily: "'Roboto', sans-serif",
    },
    notificationError: {
      backgroundColor: '#F3E8FF',
      color: '#6B21A8',
      fontFamily: "'Roboto', sans-serif",
    },
    notificationClose: {
      cursor: 'pointer',
      fontSize: '0.75rem',
      color: '#6B21A8',
      fontFamily: "'Roboto', sans-serif",
    },
    headerContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
      width: '100%',
      fontFamily: "'Roboto', sans-serif",
    },
    buttonContainer: {
      display: 'flex',
      alignItems: 'center',
      fontFamily: "'Roboto', sans-serif",
    },
    addButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '8px 19.2px',
      fontSize: '14px',
      fontWeight: '600',
      color: '#6B21A8',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      cursor: 'pointer',
      position: 'relative',
      borderRadius: '30px',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      transition: 'all 0.4s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    searchFilterGroup: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '8px',
      fontFamily: "'Roboto', sans-serif",
    },
    searchInput: {
      width: '100%',
      maxWidth: '300px',
      padding: '6px 12px',
      borderRadius: '30px',
      border: '1px solid #ddd',
      fontSize: '14px',
      outline: 'none',
      transition: 'border-color 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    searchInputFocus: {
      borderColor: '#1890ff',
    },
    filterIcon: {
      color: '#6B21A8',
      cursor: 'pointer',
      fontSize: '24px',
      fontFamily: "'Roboto', sans-serif",
    },
    filterTagsContainer: { 
      display: 'flex', 
      alignItems: 'center', 
      gap: '8px', 
      marginBottom: '16px', 
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
      fontFamily: "'Roboto', sans-serif",
    },
    selectedFilterTag: { 
      display: 'inline-flex', 
      alignItems: 'center', 
      background: 'white', 
      color: '#1F2937', 
      padding: '4px 8px',
      borderRadius: '12px', 
      fontSize: '12px', 
      marginBottom: '4px',
      fontFamily: "'Roboto', sans-serif",
    },
    removeFilterButton: { 
      marginLeft: '6px', 
      background: 'none', 
      border: 'none', 
      color: '#1F2937', 
      cursor: 'pointer', 
      fontSize: '12px',
      fontFamily: "'Roboto', sans-serif",
    },
    filterModalOverlay: { 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      width: '100%', 
      height: '100%', 
      backgroundColor: 'rgba(0, 0, 0, 0.5)', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      zIndex: 1000,
      fontFamily: "'Roboto', sans-serif",
    },
    filterModal: { 
      backgroundColor: '#f5f5f5',
      padding: '16px', 
      borderRadius: '8px', 
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)', 
      width: '100%', 
      maxWidth: '350px',
      fontFamily: "'Roboto', sans-serif",
    },
    filterModalHeader: { 
      fontSize: '16px', 
      fontWeight: '600', 
      marginBottom: '16px', 
      color: '#333',
      backgroundColor: '#f5f5f5',
      padding: '4px 8px',
      borderRadius: '4px',
      fontFamily: "'Roboto', sans-serif",
    },
    filterModalButtons: { 
      display: 'flex', 
      justifyContent: 'flex-end', 
      gap: '8px', 
      marginTop: '16px',
      fontFamily: "'Roboto', sans-serif",
    },
    modalButton: { 
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '5px 15px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#6B21A8',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      cursor: 'pointer',
      borderRadius: '30px',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      transition: 'all 0.4s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    modalButtonCancel: {
      backgroundColor: '#d0d0d0',
      color: '#374151',
      padding: '5px 15px',
      border: 'none',
      borderRadius: '30px',
      fontSize: '14px',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    modalButtonHover: {
      backgroundColor: '#c0c0c0',
      color: '#8A2BE2',
      transform: 'scale(1.03)',
      fontFamily: "'Roboto', sans-serif",
    },
    tableContainer: {
      flex: 1,
      overflowX: 'auto',
      overflowY: 'auto',
      maxHeight: 'calc(91vh - 10rem)',
      minHeight: '300px',
      width: '100%',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: "'Roboto', sans-serif",
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '12px',
      border: 'none',
      fontFamily: "'Roboto', sans-serif",
    },
    thead: {
      background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)',
      position: 'sticky',
      top: 0,
      zIndex: 10,
      fontFamily: "'Roboto', sans-serif",
    },
    th: {
      padding: '8px 10px',
      textAlign: 'center',
      fontWeight: '500',
      color: '#6B21A8',
      fontFamily: "'Roboto', sans-serif",
    },
    thSr: {
      padding: '8px 10px',
      fontWeight: '500',
      color: '#6B21A8',
      fontFamily: "'Roboto', sans-serif",
    }, 
    tbody: {
      borderBottom: '1px solid #B0BEC5',
      fontFamily: "'Roboto', sans-serif",
    },
    td: {
      padding: '10px 12px',
      color: '#1F2937',
      textAlign: 'center',
      fontFamily: "'Roboto', sans-serif",
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    tdSr: {
      padding: '10px 12px',
      color: '#1F2937',
      fontFamily: "'Roboto', sans-serif",
    },
    tdClamped: {
      padding: '18px 30px',
      color: '#1F2937',
      textAlign: 'left',
      fontFamily: "'Roboto', sans-serif",
      display: '-webkit-box',
      WebkitLineClamp: 1,
      WebkitBoxOrient: 'horizontal',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      lineHeight: '1.5em',
      maxHeight: '2.4em',
      wordBreak: 'break-word',
    },
    correctAnswer: {
      padding: '10px 12px',
      color: '#1F2937',
      textAlign: 'center',
      fontFamily: "'Roboto', sans-serif",
    },
    optionsList: {
      margin: 0,
      paddingLeft: '20px',
      fontFamily: "'Roboto', sans-serif",
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      lineHeight: '1.2em',
      maxHeight: '2.4em',
      listStyleType: 'disc',
    },
    errorContainer: {
      backgroundColor: '#F3E8FF',
      color: '#6B21A8',
      padding: '10px',
      borderRadius: '8px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      fontSize: '12px',
      animation: 'slideInFromRight 0.5s ease-out',
      fontFamily: "'Roboto', sans-serif",
    },
    errorCloseButton: {
      background: 'none',
      border: 'none',
      fontSize: '12px',
      cursor: 'pointer',
      color: '#6B21A8',
      fontFamily: "'Roboto', sans-serif",
    },
    actionButtons: { 
      display: 'flex', 
      justifyContent: 'center', 
      gap: '4px',
      fontFamily: "'Roboto', sans-serif",
    },
    viewButton: { 
      backgroundColor: 'transparent', 
      color: '#cc8888', 
      border: 'none', 
      borderRadius: '2px', 
      padding: '2px', 
      cursor: 'pointer',
      fontFamily: "'Roboto', sans-serif",
    },
    editButton: { 
      backgroundColor: 'transparent', 
      color: '#5f77cc', 
      border: 'none', 
      borderRadius: '1px', 
      padding: '1px', 
      cursor: 'pointer',
      fontFamily: "'Roboto', sans-serif",
    },
    deleteButton: { 
      backgroundColor: 'transparent', 
      color: '#7a5fcc', 
      border: 'none', 
      borderRadius: '1px', 
      padding: '1px', 
      cursor: 'pointer',
      fontFamily: "'Roboto', sans-serif",
    },
    dialogOverlay: { 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      width: '100%', 
      height: '100%', 
      backgroundColor: 'rgba(0, 0, 0, 0.5)', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      zIndex: 1000,
      fontFamily: "'Roboto', sans-serif",
    },
    viewModal: {
      backgroundColor: 'white',
      borderRadius: '0.5rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      color: '#1F2937',
      fontFamily: "'Roboto', sans-serif",
      width: '90vw',
      maxWidth: '800px',
      margin: '0 auto',
      padding: '24px',
      maxHeight: '81vh',
      overflowY: 'auto',
      overflowX: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      position: 'relative',
    },
    viewModalHeader: { 
      fontSize: '20px', 
      fontWeight: '600', 
      color: '#6B21A8',
      marginBottom: '16px',
      borderBottom: '1px solid #e5e7eb',
      paddingBottom: '8px',
      fontFamily: "'Roboto', sans-serif",
    },
    viewModalContent: { 
      fontSize: '14px', 
      color: '#1F2937', 
      lineHeight: '1.6',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      fontFamily: "'Roboto', sans-serif",
    },
    viewModalSection: {
      paddingBottom: '12px',
      borderBottom: '1px solid #e5e7eb',
      fontFamily: "'Roboto', sans-serif",
      overflowWrap: 'break-word',
      overflowX: 'hidden',
      maxWidth: '100%',
    },
    viewModalLabel: { 
      fontWeight: '600', 
      color: '#6B21A8',
      marginBottom: '4px',
      fontFamily: "'Roboto', sans-serif",
    },
    viewModalValue: {
      color: '#1F2937',
      fontFamily: "'Roboto', sans-serif",
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      whiteSpace: 'normal',
      overflowX: 'hidden',
      maxWidth: '100%',
    },
    viewModalOptionsList: {
      margin: '8px 0 0 16px',
      padding: '0',
      listStyleType: 'disc',
      fontFamily: "'Roboto', sans-serif",
    },
    viewModalCloseButton: { 
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '5px 15px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#6B21A8',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      cursor: 'pointer',
      borderRadius: '30px',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      transition: 'all 0.4s ease',
      alignSelf: 'flex-end',
      marginTop: '16px',
      fontFamily: "'Roboto', sans-serif",
    },
    notificationContainerInner: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      width: '100%',
      fontFamily: "'Roboto', sans-serif",
    },
    notificationInner: {
      fontSize: '0.85rem',
      color: '#6B21A8',
      backgroundColor: '#F3E8FF',
      padding: '5px 10px',
      borderRadius: '4px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      animation: 'slideInFromRight 0.5s ease-out',
      wordBreak: 'break-word',
      overflowX: 'hidden',
      fontFamily: "'Roboto', sans-serif",
    },
    notificationCloseInner: {
      cursor: 'pointer',
      fontSize: '0.75rem',
      color: '#6B21A8',
      fontFamily: "'Roboto', sans-serif",
    },
    paginationContainer: {
      background: '#ffffff',
      padding: '10px 0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeight: '40px',
      marginTop: '20px',
      zIndex: 5,
      fontFamily: "'Roboto', sans-serif",
    },
    pageButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '3px 13px',
      fontSize: '13px',
      fontWeight: '600',
      color: '#6B21A8',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      cursor: 'pointer',
      borderRadius: '30px',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      transition: 'all 0.4s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    pageNumberButton: {
      width: '30px',
      height: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '13px',
      fontWeight: '600',
      color: '#333',
      cursor: 'pointer',
      borderRadius: '50%',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #D3D3D3, #D3D3D3, #D3D3D3)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      transition: 'transform 0.2s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    paginationWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontFamily: "'Roboto', sans-serif",
    },
    pageSelect: {
      width: '60px',
      padding: '5px 25px 5px 10px',
      borderRadius: '5px',
      border: '1px solid #ccc',
      fontSize: '14px',
      color: '#1F2937',
      cursor: 'pointer',
      backgroundColor: '#fff',
      appearance: 'none',
      WebkitAppearance: 'none',
      MozAppearance: 'none',
      backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%236B21A8%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000%20.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2010-1.414-1.414z%22%2F%3E%3C%2Fsvg%3E")',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 10px center',
      backgroundSize: '12px',
      transition: 'border-color 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
  };

  return (
    <>
      <style>
        {`
          @keyframes glow {
            0% { box-shadow: 0 0 4px #C5A8FF; }
            50% { box-shadow: 0 0 12px #C5A8FF; }
            100% { box-shadow: 0 0 4px #C5A8FF; }
          }
          .glow-button:hover:not(:disabled) {
            background: linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF);
            transform: scale(1.05);
            animation: glow 1.5s ease-in-out infinite;
            font-family: 'Roboto', sans-serif !important;
          }
          @keyframes slideInFromRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          .add-question-btn:hover {
            transform: scale(1.05);
            animation: glow 1.5s ease-in-out infinite;
            font-family: 'Roboto', sans-serif !important;
          }
          .filter-icon:hover {
            color: #40a9ff;
            transform: scale(1.1);
            font-family: 'Roboto', sans-serif !important;
          }
          .search-input:focus {
            border-color: ${styles.searchInputFocus.borderColor};
            font-family: 'Roboto', sans-serif !important;
          }
          .custom-table {
            border: none !important;
            table-layout: fixed;
            width: 100%;
            flex: 1;
            font-family: 'Roboto', sans-serif !important;
          }
          .custom-table th,
          .custom-table td {
            border: none !important;
            font-family: 'Roboto', sans-serif !important;
          }
          .custom-table thead {
            background: linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF);
            position: sticky;
            top: 0;
            zIndex: 10;
            font-family: 'Roboto', sans-serif !important;
          }
          .custom-table tbody tr:nth-child(odd) {
            background-color: #FFFFFF;
            font-family: 'Roboto', sans-serif !important;
          }
          .custom-table tbody tr:nth-child(even) {
            background: linear-gradient(to right, #FAFCFF, #FBF6FF, #FFF8FB);
            font-family: 'Roboto', sans-serif !important;
          }
          .custom-table tbody tr:hover {
            background: linear-gradient(to right, #F0F5FF, #F5EDFF, #FFF0F5);
            font-family: 'Roboto', sans-serif !important;
          }
          .table-container {
            overflow-y: auto;
            overflow-x: auto;
            max-height: calc(90vh - 10rem);
            display: flex;
            flex-direction: column;
            font-family: 'Roboto', sans-serif !important;
          }
          .filter-modal select,
          .filter-modal button.dropdown-button,
          .filter-modal .MuiSelect-select,
          .question-modal select,
          .question-modal input,
          .question-modal textarea,
          .question-modal button,
          .question-modal label,
          .question-modal h2 {
            font-family: 'Roboto', sans-serif !important;
          }
          .view-modal::-webkit-scrollbar {
            width: 8px;
          }
          .view-modal::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
          }
          .view-modal::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 4px;
          }
          .view-modal::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
          .glow-button:not(:disabled):hover {
            background: linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF);
            transform: scale(1.05);
            animation: glow 1.5s ease-in-out infinite;
            font-family: 'Roboto', sans-serif !important;
          }
          .glow-button:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            animation: none;
            transform: none;
            font-family: 'Roboto', sans-serif !important;
          }
          .katex {
            font-size: 1em !important;
            display: inline !important;
            vertical-align: middle;
            font-family: 'Roboto', sans-serif !important;
            white-space: normal !important;
            word-break: break-word !important;
            max-width: 100% !important;
            color: #1F2937 !important; /* Ensure Katex text is black */
          }
          .katex-display {
            margin: 0 !important;
            display: block !important;
            font-family: 'Roboto', sans-serif !important;
            white-space: normal !important;
            word-break: break-word !important;
            max-width: 100% !important;
            overflow-x: hidden !important;
            color: #1F2937 !important; /* Ensure Katex block text is black */
          }
          .pagination-button:hover:not(:disabled) {
            transform: scale(1.05);
            animation: glow 1.5s ease-in-out infinite;
            font-family: 'Roboto', sans-serif !important;
          }
          .pagination-button:active:not(:disabled) {
            transform: scale(0.95);
            font-family: 'Roboto', sans-serif !important;
          }
          .page-select:focus {
            border-color: #6B21A8;
            outline: none;
            box-shadow: 0 0 5px rgba(107, 33, 168, 0.3);
            font-family: 'Roboto', sans-serif !important;
          }
          .page-select option {
            font-family: 'Roboto', sans-serif !important;
            background: #fff;
            color: #1F2937;
          }
          .page-select option:hover {
            background: linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF);
            color: #6B21A8;
            font-family: 'Roboto', sans-serif !important;
          }
          .custom-dropdown {
            position: relative;
            width: 60px;
            padding: 5px 25px 5px 10px;
            border: 1px solid #ccc;
            border-radius: 5px;
            background-color: #fff;
            color: #1F2937;
            cursor: pointer;
            font-family: 'Roboto', sans-serif !important;
            backgroundImage: url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%236B21A8%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000%20.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2010-1.414-1.414z%22%2F%3E%3C%2Fsvg%3E");
            backgroundRepeat: 'no-repeat',
            backgroundPosition: right 10px center,
            backgroundSize: 12px,
          }
          .custom-dropdown-options {
            position: absolute;
            bottom: 100%;
            left: 0;
            width: 100%;
            border: 1px solid #ccc;
            border-radius: 5px;
            background-color: #fff;
            z-index: 1000;
            max-height: 200px;
            overflow-y: auto;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            font-family: 'Roboto', sans-serif !important;
          }
          .custom-option {
            padding: 5px 10px;
            cursor: pointer;
            font-family: 'Roboto', sans-serif !important;
          }
          .custom-option:hover {
            background: #D3D3D3 !important;
            color: #1F2937 !important;
            font-family: 'Roboto', sans-serif !important;
          }
        `}
      </style>
      <div style={styles.container}>
        <AdminNavbar />
        <div style={{ ...styles.sidebar, position: 'fixed', top: 0, left: 0, zIndex: 10, transition: 'width 0.3s ease' }}>
          <AdminSidebar />
        </div>
        <div style={{ marginLeft: sidebarWidth, width: `calc(100% - ${sidebarWidth})`, transition: 'margin-left 0.3s ease', fontFamily: "'Roboto', sans-serif" }}>
          <div style={styles.content}>
            <div style={styles.notificationContainer}>
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  style={{
                    ...styles.notification,
                    ...(notification.type === 'success'
                      ? styles.notificationSuccess
                      : styles.notificationError),
                  }}
                >
                  {notification.message}
                  <FontAwesomeIcon
                    icon={faTimes}
                    style={styles.notificationClose}
                    onClick={() => handleCloseNotification(notification.id)}
                  />
                </div>
              ))}
            </div>
            <div style={styles.headerContainer}>
              <div style={styles.buttonContainer}>
                <button
                  onClick={handleAddQuestion}
                  style={styles.addButton}
                  className="add-question-btn"
                >
                  Add Question
                </button>
              </div>
              <div style={styles.searchFilterGroup}>
                <input
                  type="text"
                  placeholder="Search questions..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  style={styles.searchInput}
                  className="search-input"
                  aria-label="Search questions"
                />
                <FilterListIcon
                  style={styles.filterIcon}
                  className="filter-icon"
                  onClick={handleOpenFilterModal}
                  title="Open filter"
                  aria-label="Open filter modal"
                />
              </div>
            </div>

            {(selectedFilters.section || selectedFilters.difficulty) && (
              <div style={styles.filterTagsContainer}>
                {selectedFilters.section && (
                  <span style={styles.selectedFilterTag}>
                    Section: {selectedFilters.section.charAt(0).toUpperCase() + selectedFilters.section.slice(1)}
                    <button
                      onClick={() => removeFilter('section')}
                      style={styles.removeFilterButton}
                      aria-label="Remove section filter"
                    >
                      ✕
                    </button>
                  </span>
                )}
                {selectedFilters.difficulty && (
                  <span style={styles.selectedFilterTag}>
                    Difficulty: {selectedFilters.difficulty.charAt(0).toUpperCase() + selectedFilters.difficulty.slice(1)}
                    <button
                      onClick={() => removeFilter('difficulty')}
                      style={styles.removeFilterButton}
                      aria-label="Remove difficulty filter"
                    >
                      ✕
                    </button>
                  </span>
                )}
              </div>
            )}

            {showFilterModal && (
              <div style={styles.filterModalOverlay}>
                <div style={styles.filterModal} className="filter-modal">
                  <h2 style={styles.filterModalHeader}>Filter Questions</h2>
                  {notifications.length > 0 && (
                    <div style={styles.notificationContainerInner}>
                      {notifications.map((notification) => (
                        <div
                          key={notification.id}
                          style={styles.notificationInner}
                        >
                          {notification.message}
                          <FontAwesomeIcon
                            icon={faTimes}
                            style={styles.notificationClose}
                            onClick={() => handleCloseNotification(notification.id)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  <Filter 
                    sections={sections}
                    difficulties={difficulties}
                    selectedFilters={tempFilters}
                    onFilterChange={handleFilterChange}
                    inModal
                  />
                  <div style={styles.filterModalButtons}>
                    <button
                      onClick={handleCloseFilterModal}
                      style={styles.modalButtonCancel}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={applyFilterModal}
                      style={styles.modalButton}
                      className="glow-button"
                    >
                      SAVE
                    </button>
                  </div>
                </div>
              </div>
            )}
  
            <div style={styles.tableContainer} className="table-container" ref={tableContainerRef}>
              <table
                className="custom-table"
                style={{
                  borderRadius: '8px',
                  marginBottom: 0,
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '12px',
                  border: 'none',
                  tableLayout: 'fixed',
                }}
              >
                <thead style={styles.thead}>
                  <tr>
                    <th style={{ ...styles.thSr, width: '3%' }}>Sr.</th>
                    <th style={{ ...styles.th, textAlign: 'center', width: '27%' }}>Question</th>
                    <th style={{ ...styles.th, textAlign: 'center', width: '20%' }}>Options</th>
                    <th style={{ ...styles.th, width: '15%' }}>Correct Answer</th>
                    <th style={{ ...styles.th, width: '10%' }}>Section</th>
                    <th style={{ ...styles.th, width: '10%' }}>Difficulty</th>
                    <th style={{ ...styles.th, textAlign: 'center', width: '15%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7"
                        style={{ ...styles.td, textAlign: 'center', color: '#666' }}
                      >
                        Loading questions...
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan="7" style={{ ...styles.td, textAlign: 'center' }}>
                        <div style={styles.errorContainer}>
                          <span>{error}</span>
                          <button
                            style={styles.errorCloseButton}
                            onClick={() => setError(null)}
                            aria-label="Close error message"
                          >
                            ×
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredQuestions.length > 0 ? (
                      currentQuestions.map((q, idx) => (
                        <tr key={q._id || q}>
                          <td style={styles.tdSr}>{indexOfFirstQuestion + idx + 1}</td>
                          <td style={styles.tdClamped}>
                            {renderTextWithLatex(q.questionText || q.text, '')}
                          </td>
                          <td style={{ ...styles.td, textAlign: 'left' }}>
                            <ul style={styles.optionsList}>
                              {(q.options || []).map((opt, j) => (
                                <li key={j} style={{ listStyleType: 'disc' }}>
                                  {renderTextWithLatex(opt, false)}
                                </li>
                              ))}
                            </ul>
                          </td>
                          <td style={styles.tdClamped}>
                            {renderTextWithLatex(q.correctAnswer, '')}
                          </td>
                          <td style={styles.td}>{q.section || q.part}</td>
                          <td style={{ ...styles.td, textTransform: 'capitalize' }}>
                            {q.difficulty || 'Not specified'}
                          </td>
                          <td style={styles.td}>
                            <div style={styles.actionButtons}>
                              <button
                                onClick={() => handleView(q)}
                                style={styles.viewButton}
                                title="View"
                                aria-label="View question"
                              >
                                <VisibilityIcon style={{ fontSize: '17px', fontFamily: "'Roboto', sans-serif" }} />
                              </button>
                              <button
                                onClick={() => handleEdit(q)}
                                style={styles.editButton}
                                title="Edit"
                                aria-label="Edit question"
                              >
                                <EditIcon style={{ fontSize: '17px', fontFamily: "'Roboto', sans-serif" }} />
                              </button>
                              <button
                                onClick={() => handleDelete(q._id)}
                                style={styles.deleteButton}
                                title="Delete"
                                aria-label="Delete question"
                              >
                                <DeleteIcon style={{ fontSize: '14px', fontFamily: "'Roboto', sans-serif" }} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: '20px', fontFamily: "'Roboto', sans-serif" }}>
                          No questions found
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>   
              <div style={styles.paginationContainer}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: "'Roboto', sans-serif" }}>
                  <span style={{ fontSize: '14px', color: '#1F2937', fontFamily: "'Roboto', sans-serif" }}>
                    Showing {indexOfFirstQuestion + 1} to {Math.min(indexOfLastQuestion, filteredQuestions.length)} of {filteredQuestions.length} entries
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: "'Roboto', sans-serif" }}>
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      ...styles.pageButton,
                      backgroundColor: currentPage === 1 ? '#ccc' : undefined,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    }}
                    className="pagination-button glow-button"
                    aria-label="Previous page"
                  >
                    Previous
                  </button>
                  <div style={styles.paginationWrapper}>
                    {Array.from({ length: totalPages }, (_, i) => {
                      const pageNum = i + 1;
                      const shouldShow = 
                        pageNum === 1 || 
                        pageNum === totalPages || 
                        (pageNum >= currentPage - 2 && pageNum <= currentPage + 2);
                      
                      if (!shouldShow) {
                        if (
                          (pageNum === currentPage - 3 && currentPage > 4) || 
                          (pageNum === currentPage + 3 && currentPage < totalPages - 3)
                        ) {
                          return (
                            <span key={`ellipsis-${pageNum}`} style={{ padding: '0 5px', color: '#6B21A8', fontFamily: "'Roboto', sans-serif" }}>
                              ...
                            </span>
                          );
                        }
                        return null;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => paginate(pageNum)}
                          style={{
                            ...styles.pageNumberButton,
                            background: currentPage === pageNum ? 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)' : '#ffffff',
                            color: currentPage === pageNum ? '#6B21A8' : '#333',
                          }}
                          className={`pagination-button page-number-button ${currentPage === pageNum ? 'active' : ''}`}
                          aria-label={`Page ${pageNum}`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      ...styles.pageButton,
                      backgroundColor: currentPage === totalPages ? '#ccc' : undefined,
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    }}
                    className="pagination-button glow-button"
                    aria-label="Next page"
                  >
                    Next
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: "'Roboto', sans-serif", position: 'relative' }}>
                  <div
                    className="custom-dropdown"
                    style={{
                      ...styles.pageSelect,
                      padding: '5px 25px 5px 10px',
                      width: '60px',
                      cursor: 'pointer',
                      position: 'relative',
                    }}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    {currentPage}
                    {isDropdownOpen && (
                      <div className="custom-dropdown-options">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                          <div
                            key={pageNum}
                            className="custom-option"
                            onClick={() => {
                              paginate(pageNum);
                              setIsDropdownOpen(false);
                            }}
                          >
                            {pageNum}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <QuestionModal
              show={showModal}
              handleClose={() => setShowModal(false)}
              onSave={handleSaveQuestion}
              selectedQuestion={selectedQuestion}
              onModalCloseRequest={handleModalCloseRequest}
              notifications={notifications}
              handleCloseNotification={handleCloseNotification}
            />
            {showViewModal && selectedQuestion && (
              <div style={styles.dialogOverlay}>
                <div style={styles.viewModal} className="view-modal">
                  <h2 style={styles.viewModalHeader}>Question Details</h2>
                  {notifications.length > 0 && (
                    <div style={styles.notificationContainerInner}>
                      {notifications.map((notification) => (
                        <div
                          key={notification.id}
                          style={styles.notificationInner}
                        >
                          {notification.message}
                          <FontAwesomeIcon
                            icon={faTimes}
                            style={styles.notificationCloseInner}
                            onClick={() => handleCloseNotification(notification.id)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={styles.viewModalContent}>
                    <div style={styles.viewModalSection}>
                      <div style={styles.viewModalLabel}>Question:</div>
                      <div style={styles.viewModalValue}>
                        {renderTextWithLatex(selectedQuestion.questionText || selectedQuestion.text, false)}
                      </div>
                    </div>
                    <div style={styles.viewModalSection}>
                      <div style={styles.viewModalLabel}>Options:</div>
                      <ul style={styles.viewModalOptionsList}>
                        {(selectedQuestion.options || []).map((opt, i) => (
                          <li key={i} style={styles.viewModalValue}>
                            {renderTextWithLatex(opt, false)}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div style={styles.viewModalSection}>
                      <div style={styles.viewModalLabel}>Correct Answer:</div>
                      <div style={styles.viewModalValue}>
                        {renderTextWithLatex(selectedQuestion.correctAnswer, false)}
                      </div>
                    </div>
                    <div style={styles.viewModalSection}>
                      <div style={styles.viewModalLabel}>Section:</div>
                      <div style={styles.viewModalValue}>{selectedQuestion.section || selectedQuestion.part}</div>
                    </div>
                    <div style={styles.viewModalSection}>
                      <div style={styles.viewModalLabel}>Difficulty:</div>
                      <div style={styles.viewModalValue}>{selectedQuestion.difficulty || 'Not specified'}</div>
                    </div>
                    <div style={styles.viewModalSection}>
                      <div style={styles.viewModalLabel}>Created At:</div>
                      <div style={styles.viewModalValue}>{formatDate(selectedQuestion.createdAt)}</div>
                    </div>
                    <div style={styles.viewModalSection}>
                      <div style={styles.viewModalLabel}>Updated At:</div>
                      <div style={styles.viewModalValue}>{formatDate(selectedQuestion.updatedAt)}</div>
                    </div>
                  </div>
                  <button
                    onClick={handleCloseViewModal}
                    style={styles.viewModalCloseButton}
                    className="glow-button"
                    aria-label="Close view modal"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default QuestionManager;